'use client';

interface HiddenListGateProps {
  /** Short heading, e.g. "Members List Hidden". */
  title: string;
  /** One-line explanation, e.g. "Only group members can view the member list". */
  message: string;
  testIdPrefix: string;
  /**
   * Decorative skeleton rendered behind the blur.
   *
   * MUST be placeholder shapes, never the real list. CSS blur is makeup: the
   * nodes stay in the DOM, readable via devtools, the accessibility tree and
   * the test renderer. Passing real member or event rows here would leak
   * exactly the data the gate exists to hide.
   */
  children: React.ReactNode;
}

/**
 * MWBI-890 — the shared "this list is hidden" gate for the Members and Events
 * tabs.
 *
 * Visually matches the group Forum's gate (blur-sm plus a white card near the
 * top) so the three tabs read as one family, replacing the blur-md centred
 * EyeOff modal the two list tabs used before.
 *
 * It deliberately does NOT copy the Forum's data handling. The Forum blurs its
 * real topic list; both callers here pass empty skeletons instead, so a
 * non-member never receives the hidden rows at all. See the `children` note.
 */
export function HiddenListGate({ title, message, testIdPrefix, children }: HiddenListGateProps) {
  return (
    <div className="relative" data-testid={`${testIdPrefix}-hidden-gate`}>
      {/* Skeletons carry no information, so keep them out of the
          accessibility tree entirely rather than presenting empty boxes. */}
      <div className="blur-sm pointer-events-none select-none" aria-hidden="true">
        {children}
      </div>
      <div className="absolute inset-0 z-10 flex items-start justify-center pt-20">
        <div
          role="status"
          className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-4"
          data-testid={`${testIdPrefix}-hidden-notice`}
        >
          <h3 className="text-base font-semibold text-black mb-2">{title}</h3>
          <p className="text-sm text-gray-600">{message}</p>
        </div>
      </div>
    </div>
  );
}
